import { useCallback, useEffect, useRef, type ReactElement } from 'react';
import { Button } from '@/components/atoms/button';

const HEADING_ID = 'reject-candidate-modal-heading';

const COPY = {
  heading: 'Reject submission',
  banner:
    'This will mark the submission as rejected. The reason is logged for the audit trail and is not shown to the submitter.',
  bodyPrefix: 'Reject ',
  bodySuffix: '?',
  reasonLabel: 'Reason',
  confirm: 'Reject',
  confirming: 'Rejecting…',
  cancel: 'Cancel',
} as const;

export type RejectCandidateModalProps = {
  candidateTitle: string;
  reason: string;
  isRejecting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function RejectCandidateModal({
  candidateTitle,
  reason,
  isRejecting,
  onConfirm,
  onCancel,
}: RejectCandidateModalProps): ReactElement {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  const handleEscape = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    },
    [onCancel],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={HEADING_ID}
        className="relative w-full max-w-md space-y-5 rounded-t-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-6 shadow-xl sm:rounded-[var(--r-md)]"
      >
        <div>
          <h2
            id={HEADING_ID}
            className="font-display text-xl font-semibold tracking-tight text-[var(--ink)]"
          >
            {COPY.heading}
          </h2>
          <p className="mt-2 text-sm text-[var(--ink-3)]">{COPY.banner}</p>
          <p className="mt-3 text-sm text-[var(--ink-3)]">
            {COPY.bodyPrefix}
            <span className="font-medium text-[var(--ink)]">{candidateTitle}</span>
            {COPY.bodySuffix}
          </p>
        </div>

        <div className="rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] p-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--ink-4)]">
            {COPY.reasonLabel}
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--ink-2)]">{reason}</p>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button ref={cancelRef} variant="secondary" size="sm" onClick={onCancel}>
            {COPY.cancel}
          </Button>
          <Button variant="danger" size="sm" onClick={onConfirm} disabled={isRejecting}>
            {isRejecting ? COPY.confirming : COPY.confirm}
          </Button>
        </div>
      </div>
    </div>
  );
}
